import { Component, inject, effect, input, OnInit, output } from '@angular/core';
import { DialogComponent } from 'projects/shared/src/ui-kit/common/components/dialog/dialog.component';
import { DialogAction } from 'projects/shared/src/lib/core/interfaces/dialog';
import { DialogService } from 'projects/shared/src/lib/core/services/dialog.service';
import { IconButtonComponent } from 'projects/shared/src/ui-kit/button/components/icon-button/icon-button.component';
import { NavigationService } from 'projects/shared/src/lib/core/services/navigation.service';
import { ExerciseStore } from 'projects/shared/src/lib/store/exercise.store';
import { TranslatePipe } from '@ngx-translate/core';
import { NumpadService } from 'projects/shared/src/lib/core/services/numpad.service';
import { DialogKeys } from '../../core/enums/dialog-keys.enum';
import { PauseDialogCoordinator } from './pause-dialog.coordinator';

@Component({
  selector: 'app-pause-dialog',
  imports: [DialogComponent, IconButtonComponent, TranslatePipe],
  templateUrl: './pause-dialog.component.html',
  styleUrl: './pause-dialog.component.scss',
})
export class PauseDialogComponent implements OnInit {
  openButtonLabel = input<string>();
  openButtonIcon = input<string>();
  pause = output<void>();
  
  public dialogService = inject(DialogService);
  public store = inject(ExerciseStore);
  public DialogKeys = DialogKeys;

  public modalActions: DialogAction[] = [
    {
      label: 'TaskPauseDialog.HomeTask',
      action: () => this.handleStopTask(),
      type: 'secondary',
      icon: 'ph ph-house ph-bold',
    },
    {
      label: 'TaskPauseDialog.ContinueBtn',
      action: () => this.continueTask(),
      type: 'primary',
      icon: 'ph ph-play ph-bold',
    },
  ];

  private readonly navigationService = inject(NavigationService);
  private readonly numpadService = inject(NumpadService);
  // Coordinator to keep event handlers in one place when many pause-dialog components visible
  private readonly coordinator = inject(PauseDialogCoordinator);
  private lastFocusedElement: HTMLElement | null = null;

  constructor() {
    effect(() => {
      const isVisible = this.dialogService.visible();
      if (!isVisible && this.lastFocusedElement) {
        setTimeout(() => {
          this.numpadService.moveCursorToEnd(this.lastFocusedElement);
        }, 0);
      }
    });
  }

  // because this pause-dialog is used in 2 location
  // otherwise the dialog inside uses the same key and is opened twice
  public dialogKey: string = DialogKeys.Pause

  ngOnInit(): void {
    this.dialogKey += (this.openButtonLabel ?? this.openButtonIcon ?? '');
  }

  public open() {
    if (this.coordinator.isAnyOpen()) return;

    this.lastFocusedElement = this.numpadService.focusedInputElement;

    this.dialogService.open(this.dialogKey, {
      title: 'TaskPauseDialog.Title',
      description: 'TaskPauseDialog.Content',
      actions: this.modalActions,
      onClose: () => {
        this.coordinator.onClosed(this.dialogKey);
      }
    });

    this.coordinator.onOpened(this.dialogKey);
  }

  public async handleStopTask() {
    this.pause.emit();
    
    await this.coordinator.runStopHandler(this.dialogKey);
    
    this.goToHomePage();
  }

  public goToHomePage() {
    this.store.reset();
    this.navigationService.enableBypass();
    this.navigationService.navigateToHomePage();
    this.dialogService.close();
    this.coordinator.onClosed(this.dialogKey);
  }

  public continueTask() {
    this.dialogService.close();
    this.coordinator.onClosed(this.dialogKey);
  }
}
